import { daysApart } from "@bl/domain";
import { Link, useNavigate } from "@tanstack/react-router";
import { CalendarPlus, Ellipsis, EyeOff, TriangleAlert } from "lucide-react";
import { type CSSProperties, useState } from "react";
import { type Tank, type TankStay, useBrews, useCellarSettings, useTanks } from "../api";
import { useMedia } from "../components/DocumentViewer";
import { CellarIcon, HiddenTanksIcon } from "../components/icons";
import { TankFloor } from "../components/TankFloor";
import {
  Empty,
  ErrorNote,
  Menu,
  MenuItem,
  Toolbar,
  ToolbarButton,
  usePageTitle,
} from "../components/ui";
import { showDate, today } from "../format";
import { useLayout } from "../layout/LayoutProvider";
import { byKind, capacityLabel, contentsOf, inCellar, kindColor, kindPlural } from "../tankLayout";
import { StayWarnings } from "./BrewTanks";
import { beerName } from "./Repitch";
import { PlanBrewDialog } from "./Schedule";
import { TankSchedule } from "./TankSchedule";

/**
 * Production's own page, which the sidebar's Production opens (Tyler, 2026-10-05: "this is the
 * production view"): the tank schedule in a pane on top, a row per tank and a column per day, and
 * under it the cellar, each tank with the beer in it, how long it has been there and when it is due
 * out, and what is booked into it next (Scott's Jam, 2026-10-03: "so we know that the tank is full").
 * It stands in for the Calendar and Brew day (Scott's Jam, 2026-10-08: "Calendar has been replaced by
 * the default production tab. active brew days should be accessible by clicking the beer in the
 * production tab"): a brew opens from its bar or its tank, and the brews with stock to assign are said
 * at the top.
 *
 * The title bar has Add a brew, the tanks hidden from the schedule, and Manage cellar, which opens
 * Settings at the cellar (Tyler, 2026-10-05).
 */
export function ProductionPage() {
  usePageTitle("Production");
  const { data: tanks = [], isLoading, error } = useTanks();
  const { data: settings } = useCellarSettings();
  const { layout, update } = useLayout();
  const navigate = useNavigate();
  const cellar = inCellar(tanks);
  const placed = cellar.some((t) => t.layout);
  const [view, setView] = useState<"list" | "layout">("list");
  const [planning, setPlanning] = useState<{ on?: string; tankId?: string } | null>(null);
  const shown = placed ? view : "list";
  const p = layout.production;
  // Tanks the brewery hid that are still in the cellar; one retired since no longer counts.
  const hiddenCount = cellar.filter((t) => t.hidden).length;
  const phone = useMedia("(max-width: 760px)");
  const toggleHidden = () =>
    update((l) => ({
      ...l,
      production: { ...l.production, showHidden: !l.production.showHidden },
    }));

  return (
    <>
      {/* Making things on the left, viewing them on the right (the Same Place Rule, DESIGN.md). */}
      <Toolbar
        left={
          <ToolbarButton
            icon={<CalendarPlus size={20} />}
            label="Add a brew"
            labeled
            title="Put a brew of a recipe on the calendar, in a fermenter"
            onClick={() => setPlanning({})}
          />
        }
        right={
          phone ? (
            // On a phone the two tank glyphs share one button, so Add a brew keeps its words (the
            // re-critique, 2026-10-07).
            <Menu icon={<Ellipsis size={20} />} label="More" labeled>
              {(close) => (
                <>
                  <MenuItem
                    checked={p.showHidden}
                    disabled={hiddenCount === 0 && !p.showHidden}
                    onClick={toggleHidden}
                  >
                    {hiddenCount === 0
                      ? "No tanks hidden"
                      : `Show the ${hiddenCount} hidden ${hiddenCount === 1 ? "tank" : "tanks"}`}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      close();
                      navigate({ to: "/settings", hash: "cellar" });
                    }}
                  >
                    Manage cellar
                  </MenuItem>
                </>
              )}
            </Menu>
          ) : (
            <>
              <ToolbarButton
                icon={<HiddenTanksIcon size={20} />}
                label="Hidden tanks"
                badge={hiddenCount > 0 && !p.showHidden}
                aria-pressed={p.showHidden}
                disabled={hiddenCount === 0 && !p.showHidden}
                title={
                  hiddenCount === 0
                    ? "No tanks hidden. Hide one with the eye beside its name on the schedule."
                    : p.showHidden
                      ? `Showing the ${hiddenCount} hidden, greyed out. Press to hide them again.`
                      : `${hiddenCount} hidden from the schedule for everyone. Press to show them, greyed out.`
                }
                onClick={toggleHidden}
              />
              <ToolbarButton
                icon={<CellarIcon size={20} />}
                label="Manage cellar"
                title="Add tanks, or take one out of service, under Settings"
                onClick={() => navigate({ to: "/settings", hash: "cellar" })}
              />
            </>
          )
        }
      />
      <div className="page-body">
        <h1 className="page-title">Production</h1>
        <StockToAssign />
        {cellar.length > 0 ? (
          <TankSchedule tanks={tanks} onPlan={(on, tankId) => setPlanning({ on, tankId })} />
        ) : null}
        <div className="cellar-head">
          <h2 className="brew-heading">Cellar</h2>
          {placed ? (
            <div className="segmented">
              <button type="button" aria-pressed={shown === "list"} onClick={() => setView("list")}>
                List
              </button>
              <button
                type="button"
                aria-pressed={shown === "layout"}
                onClick={() => setView("layout")}
              >
                Layout
              </button>
            </div>
          ) : null}
        </div>
        <ErrorNote error={error} />
        {isLoading ? <p className="muted">Loading</p> : null}
        {!isLoading && cellar.length === 0 ? (
          <Empty title="No tanks yet">
            <p>
              Add your fermenters and brite tanks under{" "}
              <Link to="/settings" hash="cellar">
                Settings, Equipment
              </Link>
              , then put each brew in one on brew day.
            </p>
          </Empty>
        ) : null}
        {shown === "layout" ? (
          <>
            <TankFloor
              tanks={cellar}
              width={settings?.layoutWidth}
              spotOf={(t) => t.layout}
              state={(t) => {
                const { now, next } = contentsOf(t);
                return now.length ? "in" : next ? "booked" : "empty";
              }}
              caption={(t) => contentsOf(t).now[0]?.brew.recipe.name ?? "Empty"}
              linkOf={(t) => contentsOf(t).now[0]?.brew.id ?? null}
            />
            {cellar.some((t) => !t.layout) ? (
              <p className="faint">
                Not on the layout yet:{" "}
                {cellar
                  .filter((t) => !t.layout)
                  .map((t) => t.name)
                  .join(", ")}
                .
              </p>
            ) : null}
          </>
        ) : (
          byKind(cellar).map((group) => (
            <section key={group.kind} className="cellar-group">
              <h3>
                {kindPlural(group.kind)} <span className="faint">{group.tanks.length}</span>
              </h3>
              <div className="tank-cards">
                {group.tanks.map((t) => (
                  <TankCard key={t.id} tank={t} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
      <PlanBrewDialog
        open={planning !== null}
        on={planning?.on}
        tankId={planning?.tankId}
        onClose={() => setPlanning(null)}
      />
    </>
  );
}

/**
 * Brews marked Brewed with stock inventory did not have, each flagged until that stock is given its lot
 * (Scott, 2026-10-02).
 */
export function StockToAssign() {
  const { data: brews = [] } = useBrews();
  const waiting = brews.filter((b) => b.status === "brewed" && b.toAssign.length > 0);
  if (waiting.length === 0) return null;
  return (
    <div className="notice warn production-assign" role="status">
      <TriangleAlert size={16} aria-hidden />
      <div>
        <strong>Stock to assign</strong>
        <ul>
          {waiting.map((b) => (
            <li key={b.id}>
              <Link to="/brews/$brewId" params={{ brewId: b.id }}>
                {beerName({ recipeName: b.recipe.name, number: b.number })}
              </Link>
              : {b.toAssign.join(", ")}
            </li>
          ))}
        </ul>
        <span className="muted">
          Brewed with stock that was not in inventory. Once it is received or counted, open the brew
          and click Assign to a lot under each ingredient.
        </span>
      </div>
    </div>
  );
}

function TankCard({ tank: t }: { tank: Tank }) {
  const { now, next } = contentsOf(t);
  return (
    <div
      className={`tank-card ${now.length ? "holding" : ""} ${t.outOfService ? "out-of-service" : ""}`}
      style={{ "--tank": kindColor(t.kind) } as CSSProperties}
    >
      <div className="tank-card-head">
        <strong>{t.name}</strong>
        <span className="faint">{capacityLabel(t)}</span>
        {t.outOfService ? <span className="chip warn">Out of service</span> : null}
        {t.hidden ? <HiddenChip /> : null}
      </div>
      {now.length ? now.map((s) => <InTank key={s.id} stay={s} />) : <p className="muted">Empty</p>}
      {next ? (
        <p className="faint">
          Booked from {showDate(next.inOn)} for{" "}
          <Link to="/brews/$brewId" params={{ brewId: next.brew.id }}>
            {next.brew.recipe.name}
          </Link>
        </p>
      ) : null}
    </div>
  );
}

/**
 * A tank hidden from the tank schedule says so wherever else it is listed (Tyler, 2026-10-05: "when a
 * tank is hidden it should show that"), since it still holds beer and takes bookings.
 */
export function HiddenChip() {
  return (
    <span
      className="chip plain hidden-chip"
      title="Hidden from the tank schedule, for everyone at the brewery. Show it again with the eye beside its name there, or under Change in Settings."
    >
      <EyeOff size={12} aria-hidden />
      Hidden from schedule
    </span>
  );
}

function InTank({ stay: s }: { stay: TankStay }) {
  const days = daysApart(s.inOn, today());
  return (
    <div className="tank-beer">
      <Link to="/brews/$brewId" params={{ brewId: s.brew.id }} className="tank-beer-name">
        {beerName({ recipeName: s.brew.recipe.name, number: s.brew.number })}
      </Link>
      <span className="muted">
        {days <= 0 ? "In today" : `${days} ${days === 1 ? "day" : "days"} in`}
        {s.dueOutOn ? `, due out ${showDate(s.dueOutOn)}` : ""}
      </span>
      {s.overdue ? <span className="chip warn">Overdue</span> : null}
      <StayWarnings stay={s} />
    </div>
  );
}
